import { __assign, __extends } from "tslib";
import * as React from 'react';
import { styled, classNamesFunction, FocusZone, getId, KeyCodes } from 'office-ui-fabric-react';
import { warnDependent } from '@m365-admin/utilities';
import { getNavLinkGroupStyles } from './NavLinkGroup.styles';
import { NavLink } from './NavLink';
/**
 * In order to avoid circular dependecy issues for consumers using AMD,
 * we are moving the base and main component into the same file. This avoids
 * the bundling issue AMD users are hitting. However, for most components this
 * will not be appropriate and is not recommended from a structuring stand point.
 */
var getClassNames = classNamesFunction();
var idIncrementer = 0;
var NavLinkGroupBase = /** @class */ (function (_super) {
    __extends(NavLinkGroupBase, _super);
    function NavLinkGroupBase(props) {
        var _this = _super.call(this, props) || this;
        _this.state = {
            isExpanded: !!(props.isExpanded !== undefined ? props.isExpanded : props.defaultIsExpanded),
            isKeyboardExpanded: false
        };
        _this.navLinkGroupRef = React.createRef();
        _this.navRootRef = React.createRef();
        _this._onLinkClicked = _this._onLinkClicked.bind(_this);
        _this._offsetUpdated = _this._offsetUpdated.bind(_this);
        _this._nestedNavBlur = _this._nestedNavBlur.bind(_this);
        _this._escapeSubNavFocus = _this._escapeSubNavFocus.bind(_this);
        _this.instanceNumber = idIncrementer++;
        _this.uniqueId = props.isGroupSectionHeader ? getId('Group_') : getId('L1Content_');
        return _this;
    }
    NavLinkGroupBase.prototype.render = function () {
        var _this = this;
        var _a = this.props, link = _a.link, isNavCollapsed = _a.isNavCollapsed, styles = _a.styles, theme = _a.theme, itemTotal = _a.itemTotal, itemStartIndex = _a.itemStartIndex, isCollapsibleSection = _a.isCollapsibleSection, isGroupSectionHeader = _a.isGroupSectionHeader;
        var isKeyboardExpanded = this.state.isKeyboardExpanded;
        var isExpanded = this.props.isExpanded === undefined ? this.state.isExpanded : this.props.isExpanded;
        var hasSelectedNestedLinkInGroup = link.links &&
            link.links.some(function (nestedlink) { return nestedlink.isSelected || _this._isNestedLinkSelected(nestedlink); });
        // Is L1 header under group section header
        var isL1UnderGroup = isCollapsibleSection && !isGroupSectionHeader;
        var classNames = getClassNames(styles, {
            isExpanded: isExpanded,
            isNavCollapsed: isNavCollapsed,
            isKeyboardExpanded: isKeyboardExpanded,
            theme: theme,
            isCollapsibleSection: isL1UnderGroup
        });
        var NestedComponent = isNavCollapsed && isKeyboardExpanded && !isL1UnderGroup ? FocusZone : 'ul';
        var menuId = this.uniqueId + 'menu_id' + this.instanceNumber;
        var linkId = this.uniqueId + '_id' + this.instanceNumber;
        return (React.createElement("div", __assign({ className: classNames.root, role: "presentation" }, (isNavCollapsed && link.links && { onMouseEnter: this._offsetUpdated, ref: this.navRootRef })),
            React.createElement(NavLink, __assign({ item: link, role: "menuitem", primaryIconName: link.icon }, link, { isNavCollapsed: isNavCollapsed, onClick: this._onLinkClicked, "aria-controls": menuId, "aria-expanded": isExpanded }, (isNavCollapsed &&
                !isL1UnderGroup &&
                link.links && {
                'aria-haspopup': true,
                'aria-expanded': isKeyboardExpanded
            }), { isSelected: hasSelectedNestedLinkInGroup, hasSelectedNestedLink: hasSelectedNestedLinkInGroup, hasNestedMenu: true, isCollapsibleSection: isL1UnderGroup, isExpanded: isExpanded, id: linkId, "aria-setsize": itemTotal, "aria-posinset": itemStartIndex }, (!isExpanded && hasSelectedNestedLinkInGroup && { 'aria-current': 'page' }))),
            link.links && (React.createElement("div", __assign({ className: classNames.nestedNav, role: "presentation" }, (isNavCollapsed && { ref: this.navLinkGroupRef, 'data-is-focusable': false })),
                isNavCollapsed && !isL1UnderGroup && (React.createElement(NavLink, { isNavCollapsed: isNavCollapsed, name: link.name, "data-is-focusable": false, "aria-hidden": true, primaryIconName: link.icon, disabled: true, styles: {
                        root: classNames.nestedNavHeaderItem,
                        text: classNames.nestedNavHeaderItemText,
                        icon: classNames.nestedNavHeaderItemIcon
                    } })),
                React.createElement("div", { className: classNames.nestedNavLinksWrapper, role: "presentation" },
                    React.createElement(NestedComponent, __assign({ className: classNames.nestedNavLinks, "aria-labelledby": linkId, role: "menu", id: menuId }, (isKeyboardExpanded &&
                        isNavCollapsed && {
                        componentRef: this._keyboardFocusSubNav,
                        onKeyDown: this._escapeSubNavFocus,
                        isCircularNavigation: !isL1UnderGroup,
                        as: 'ul',
                        onBlur: this._nestedNavBlur
                    })), link.links.map(function (nestedLink, index) {
                        // warn if developer passes in onExpanded without dependent links prop
                        warnDependent('INavLink', nestedLink, { onExpanded: 'links' });
                        var hasSelectedNestedLink = _this._isNestedLinkSelected(nestedLink);
                        return (React.createElement("li", { role: "presentation", key: nestedLink.key }, nestedLink.links ? (React.createElement(NavLinkGroup, __assign({}, nestedLink, { styles: nestedLink.navLinkGroupStyles, isNavCollapsed: isNavCollapsed, link: nestedLink, hasNestedMenu: true, hasSelectedNestedLink: hasSelectedNestedLink, onCollapse: _this.props.onCollapse, navRef: _this.props.navRef, focusZoneRef: _this.props.focusZoneRef, itemStartIndex: index + 1, itemTotal: link.links.length, isCollapsibleSection: isCollapsibleSection }))) : (React.createElement(NavLink, __assign({ item: nestedLink, "aria-setsize": link.links.length, "aria-posinset": index + 1, role: "menuitem", primaryIconName: nestedLink.icon }, nestedLink, { isNavCollapsed: isNavCollapsed }, (nestedLink.isSelected && { 'aria-current': 'page' }), { hasNestedMenu: false, hasSelectedNestedLink: false, isNested: isGroupSectionHeader ? false : true, isParentExpanded: isExpanded, isCollapsibleSection: isCollapsibleSection, styles: {
                                iconContainer: classNames.nestedNavLinkIconContainer
                            } })))));
                    })))))));
    };
    NavLinkGroupBase.prototype._onLinkClicked = function (ev, item) {
        var _this = this;
        this.setState(__assign(__assign({}, (this.props.isNavCollapsed && { isKeyboardExpanded: !this.state.isKeyboardExpanded })), (this.props.isExpanded === undefined && {
            isExpanded: !this.state.isExpanded
        })), function () {
            if (_this.props.onCollapse) {
                _this.props.onCollapse();
            }
        });
        if (this.props.isNavCollapsed) {
            this._offsetUpdated();
        }
        if (this.props.onExpanded) {
            this.props.onExpanded(!this.state.isExpanded);
        }
        if (this.props.link.onClick) {
            this.props.link.onClick(ev, item);
        }
    };
    // We're using the ref callback to focus the element so we can guarantee the element exists
    NavLinkGroupBase.prototype._keyboardFocusSubNav = function (focusZone) {
        if (focusZone) {
            focusZone.focus(true);
        }
    };
    NavLinkGroupBase.prototype._escapeSubNavFocus = function (event) {
        if (event.which === KeyCodes.escape) {
            this.setState({
                isKeyboardExpanded: false
            });
            if (this.props.focusZoneRef.current) {
                this.props.focusZoneRef.current.focus();
            }
        }
    };
    NavLinkGroupBase.prototype._nestedNavBlur = function (event) {
        var relatedTarget = event.relatedTarget;
        if (event.relatedTarget === null) {
            // In IE11, due to lack of support, event.relatedTarget is always
            // null making every onBlur call to be "outside" of the Nav
            // even when it's not. Using document.activeElement is another way
            // for us to be able to get what the relatedTarget without relying
            // on the event
            relatedTarget = document.activeElement;
        }
        if (!event.currentTarget.contains(relatedTarget)) {
            this.setState({
                isKeyboardExpanded: false
            });
        }
    };
    // calculate the offset due to scroll so we always position the sub nav correctly
    NavLinkGroupBase.prototype._offsetUpdated = function (_ev) {
        if (this.navRootRef.current && this.navLinkGroupRef.current && this.props.navRef.current) {
            this.navLinkGroupRef.current.style.top =
                this.navRootRef.current.offsetTop - this.props.navRef.current.scrollTop + 'px';
        }
    };
    NavLinkGroupBase.prototype._isNestedLinkSelected = function (link) {
        return (link &&
            !!link.links &&
            link.links.some(function (childLink) {
                return !!childLink && !!childLink.isSelected;
            }));
    };
    return NavLinkGroupBase;
}(React.Component));
export { NavLinkGroupBase };
export var NavLinkGroup = styled(NavLinkGroupBase, getNavLinkGroupStyles, undefined, { scope: 'NavLinkGroup' });
//# sourceMappingURL=NavLinkGroup.js.map